import { create } from "zustand";

interface UiState {
  sidebarOpen: boolean;
  theme: "light" | "dark" | "system";
  plannerFocusMode: boolean;
  sidebarOpenBeforePlannerFocus: boolean | null;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: "light" | "dark" | "system") => void;
  togglePlannerFocusMode: () => void;
  setPlannerFocusMode: (enabled: boolean) => void;
}



const applyThemeToRoot = (theme: "light" | "dark" | "system") => {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  if (theme === "system") {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    root.classList.add(prefersDark ? "dark" : "light");
    return;
  }
  root.classList.add(theme);
}



const getInitialSidebarOpen = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(min-width: 768px)").matches;
}



const getInitialTheme = (): "light" | "dark" | "system" => {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

export const useUiStore = create<UiState>((set) => ({
  sidebarOpen: getInitialSidebarOpen(),
  theme: getInitialTheme(),
  plannerFocusMode: false,
  sidebarOpenBeforePlannerFocus: null,
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  setTheme: (theme) => {
    localStorage.setItem("theme", theme);
    applyThemeToRoot(theme);
    set({ theme });
  },
  togglePlannerFocusMode: () =>
    set((s) => {
      if (s.plannerFocusMode) {
        return {
          plannerFocusMode: false,
          sidebarOpen: s.sidebarOpenBeforePlannerFocus ?? s.sidebarOpen,
          sidebarOpenBeforePlannerFocus: null,
        };
      }
      return {
        plannerFocusMode: true,
        sidebarOpenBeforePlannerFocus: s.sidebarOpen,
        sidebarOpen: false,
      };
    }),
  setPlannerFocusMode: (enabled) =>
    set((s) => {
      if (enabled === s.plannerFocusMode) return s;
      if (enabled) {
        return {
          plannerFocusMode: true,
          sidebarOpenBeforePlannerFocus: s.sidebarOpen,
          sidebarOpen: false,
        };
      }
      return {
        plannerFocusMode: false,
        sidebarOpen: s.sidebarOpenBeforePlannerFocus ?? s.sidebarOpen,
        sidebarOpenBeforePlannerFocus: null,
      };
    }),
}));

// Applique le mode dès le chargement, y compris après un refresh.
if (typeof window !== "undefined") {
  applyThemeToRoot(getInitialTheme());
}
